(() => {
  const initMobileMenus = (root = document) => {
    root.querySelectorAll('[data-mobile-toggle]').forEach((button) => {
      if (button.dataset.kineticBound === 'true') return;
      button.dataset.kineticBound = 'true';
      button.addEventListener('click', () => {
        const id = button.getAttribute('aria-controls');
        const panel = document.getElementById(id);
        if (!panel) return;
        panel.classList.toggle('hidden');
        panel.classList.toggle('is-open');
        button.setAttribute('aria-expanded', panel.classList.contains('is-open') ? 'true' : 'false');
      });
    });
  };

  const initQuantityControls = (root = document) => {
    root.querySelectorAll('[data-quantity-control]').forEach((control) => {
      if (control.dataset.kineticBound === 'true') return;
      control.dataset.kineticBound = 'true';
      const input = control.querySelector('[data-qty-input]');
      const minus = control.querySelector('[data-qty-minus]');
      const plus = control.querySelector('[data-qty-plus]');
      if (!input) return;
      minus?.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value || '1', 10) - 1)); });
      plus?.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value || '1', 10) + 1)); });
    });
  };

  const initProductGalleries = (root = document) => {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryBound === 'true') return;
      gallery.dataset.galleryBound = 'true';

      const mainImage = gallery.querySelector('.kinetic-product-gallery-image');
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      const openButton = gallery.querySelector('[data-gallery-open]');
      const prevButton = gallery.querySelector('[data-gallery-prev]');
      const nextButton = gallery.querySelector('[data-gallery-next]');
      const lightbox = gallery.querySelector('[data-gallery-lightbox]');
      const lightboxImage = gallery.querySelector('[data-gallery-lightbox-image]');
      const zoomStage = gallery.querySelector('[data-gallery-zoom-stage]');
      const zoomLabel = gallery.querySelector('[data-gallery-zoom-label]');
      let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      let zoom = 1;
      let previousFocus = null;

      if (!mainImage || !thumbs.length) return;

      const normalizeIndex = (index) => {
        if (!thumbs.length) return 0;
        return (index + thumbs.length) % thumbs.length;
      };

      const resetZoom = () => {
        zoom = 1;
        if (lightboxImage) lightboxImage.style.transform = 'scale(1)';
        if (zoomLabel) zoomLabel.textContent = '100%';
        zoomStage?.classList.remove('is-zoomed');
      };

      const setZoom = (value) => {
        zoom = Math.min(3, Math.max(1, value));
        if (lightboxImage) lightboxImage.style.transform = `scale(${zoom})`;
        if (zoomLabel) zoomLabel.textContent = `${Math.round(zoom * 100)}%`;
        zoomStage?.classList.toggle('is-zoomed', zoom > 1);
      };

      const selectImage = (index, { focusThumb = false } = {}) => {
        activeIndex = normalizeIndex(index);
        const thumb = thumbs[activeIndex];
        if (!thumb) return;
        const src = thumb.dataset.imageSrc;
        const zoomSrc = thumb.dataset.imageZoom || src;
        const alt = thumb.dataset.imageAlt || '';

        mainImage.classList.add('is-changing');
        const preloader = new Image();
        preloader.onload = () => {
          mainImage.src = src;
          mainImage.srcset = '';
          mainImage.alt = alt;
          requestAnimationFrame(() => mainImage.classList.remove('is-changing'));
        };
        preloader.onerror = () => mainImage.classList.remove('is-changing');
        preloader.src = src;

        if (lightboxImage) {
          lightboxImage.src = zoomSrc;
          lightboxImage.alt = alt;
        }
        resetZoom();

        thumbs.forEach((item, i) => {
          const selected = i === activeIndex;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        if (focusThumb) thumb.focus({ preventScroll: true });
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => selectImage(index));
        thumb.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            selectImage(index + 1, { focusThumb: true });
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            selectImage(index - 1, { focusThumb: true });
          }
        });
      });

      prevButton?.addEventListener('click', () => selectImage(activeIndex - 1));
      nextButton?.addEventListener('click', () => selectImage(activeIndex + 1));

      const openLightbox = () => {
        if (!lightbox) return;
        previousFocus = document.activeElement;
        const activeThumb = thumbs[activeIndex];
        if (lightboxImage && activeThumb) {
          lightboxImage.src = activeThumb.dataset.imageZoom || activeThumb.dataset.imageSrc;
          lightboxImage.alt = activeThumb.dataset.imageAlt || '';
        }
        resetZoom();
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('kinetic-lightbox-open');
        requestAnimationFrame(() => lightbox.classList.add('is-open'));
        lightbox.querySelector('[data-gallery-close]')?.focus();
      };

      const closeLightbox = () => {
        if (!lightbox || lightbox.hidden) return;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('kinetic-lightbox-open');
        window.setTimeout(() => { lightbox.hidden = true; }, 180);
        resetZoom();
        previousFocus?.focus?.();
      };

      openButton?.addEventListener('click', openLightbox);
      gallery.querySelectorAll('[data-gallery-close]').forEach((button) => button.addEventListener('click', closeLightbox));
      gallery.querySelector('[data-gallery-lightbox-prev]')?.addEventListener('click', () => selectImage(activeIndex - 1));
      gallery.querySelector('[data-gallery-lightbox-next]')?.addEventListener('click', () => selectImage(activeIndex + 1));
      gallery.querySelector('[data-gallery-zoom-in]')?.addEventListener('click', () => setZoom(zoom + 0.5));
      gallery.querySelector('[data-gallery-zoom-out]')?.addEventListener('click', () => setZoom(zoom - 0.5));
      gallery.querySelector('[data-gallery-zoom-reset]')?.addEventListener('click', resetZoom);

      lightboxImage?.addEventListener('click', () => setZoom(zoom >= 2 ? 1 : 2));

      lightbox?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeLightbox();
        if (event.key === 'ArrowLeft') selectImage(activeIndex - 1);
        if (event.key === 'ArrowRight') selectImage(activeIndex + 1);
        if (event.key === '+' || event.key === '=') setZoom(zoom + 0.5);
        if (event.key === '-') setZoom(zoom - 0.5);
      });

      // Horizontal swipe on the main image for mobile/tablet.
      let touchStartX = null;
      openButton?.addEventListener('touchstart', (event) => {
        touchStartX = event.touches?.[0]?.clientX ?? null;
      }, { passive: true });
      openButton?.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const endX = event.changedTouches?.[0]?.clientX ?? touchStartX;
        const delta = endX - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) < 45) return;
        if (delta < 0) selectImage(activeIndex + 1);
        else selectImage(activeIndex - 1);
      }, { passive: true });

      // Keep the gallery aligned with a variant's featured image when variants change.
      const productSection = gallery.closest('.shopify-section') || document;
      const variantSelect = productSection.querySelector('select[name="id"]');
      variantSelect?.addEventListener('change', () => {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const mediaId = selectedOption?.dataset?.featuredMediaId;
        if (!mediaId) return;
        const index = thumbs.findIndex((thumb) => thumb.dataset.imageId === mediaId);
        if (index >= 0) selectImage(index);
      });
    });
  };

  const initAll = (root = document) => {
    initMobileMenus(root);
    initQuantityControls(root);
    initProductGalleries(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
